import {
    captureException,
    captureMessage,
    init as initSentry,
    setUser,
    thirdPartyErrorFilterIntegration,
    withScope,
} from '@sentry/react';

import determineEnvironment from './environmentUtils';

const ignoreErrors = [
    'No current user',
    '_AutofillCallbackHandler',
    '__AutoFillPopupClose__',
    'Non-Error promise rejection captured with value: Object Not Found Matching Id:',
    /No current user/,
];

/**
 * Customizes ui.click breadcrumbs to include the target element text and page path
 * See https://docs.sentry.io/platforms/javascript/enriching-events/breadcrumbs/#customize-breadcrumbs
 * @param {object} breadcrumb
 * @param {object} hint
 * @returns  {object} breadcrumb
 */
export const beforeBreadcrumb = (breadcrumb, hint) => {
    try {
        if (breadcrumb.category === 'ui.click') {
            const target = hint?.event?.target;
            const elementContent = target?.innerText
                ? `with innerText ${target.innerText.slice(0, 50)}`
                : 'with no innerText';
            // eslint-disable-next-line no-param-reassign
            breadcrumb.message = `click on ${target?.tagName?.toLowerCase()} element ${elementContent} on page ${
                window?.location?.pathname
            }`;
        }
        // eslint-disable-next-line no-empty
    } catch (e) {}

    return breadcrumb;
};

/**
 * Initializes Sentry with dynamic environment setting
 */
function initialize() {
    const { VITE_APP_VERSION: viteAppVersion } = import.meta.env;
    const release = viteAppVersion ?? 'development';
    const environment = determineEnvironment();

    initSentry({
        dsn: 'https://4dabafa8ef08f240cef3b35c51f67b8e@o4506614985261056.ingest.sentry.io/4506615092215808',
        tracesSampleRate: 0,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,
        environment,
        release,
        ignoreErrors,
        integrations: [
            thirdPartyErrorFilterIntegration({
                filterKeys: ['mm-sentry-boysenberry'],
                behaviour: 'drop-error-if-contains-third-party-frames',
            }),
        ],
        beforeBreadcrumb,
    });
}

/**
 * Logs an error to Sentry
 * @param {Error} error - The error object to log
 * @param {Object} [context=null] - Additional context information for the error
 */
function logError(error, context = null) {
    withScope((scope) => {
        if (context) {
            Object.keys(context).forEach((key) => {
                scope.setExtra(key, context[key]);
            });
        }
        captureException(error);
    });
}

/**
 * Logs a message to Sentry
 * Additional data can be included using the extra property.
 * @param {String} message - The message to log
 * @param {Object} context Additional context information for the message
 * @param {string} context.level The level of the message, "fatal", "error", "warning", "log", "info", and "debug"
 * @param {Object} context.extra Additional data to include in the message
 * @example logMessage('This is a message', { level: 'warning', extra: { foo: 'bar' } })
 */
function logMessage(message, context = { level: 'info' }) {
    captureMessage(message, context);
}

/**
 * Sets the user ID for Sentry
 * @param {string} id - The ID of the user
 */
function setUserId(id) {
    setUser({ id });
}

/**
 * Clears the user information in Sentry
 */
function clearUser() {
    setUser(null);
}

export { initialize, logError, logMessage, setUserId, clearUser, ignoreErrors };
